import {useState, useEffect, useMemo} from "react"
import {
  ThreadWithProfiles,
  EmailThread,
} from "@/utils/graphQL/queries/threads/shared"
import {
  emailThreadsStub,
  getStubThreadsByMonth,
  searchStubThreads,
} from "@/data/emailThreadsStub"
import {isStubEnabled} from "@/config/stubConfig"

// Use the stub config for threads
export const USE_STUB_DATA = isStubEnabled("threads")

/**
 * Hook to use stub thread data instead of real data
 *
 * This mimics the behavior of the useGetNavigationThreads hook
 */
export function useStubNavigationThreads(itemsPerPage: number = 30) {
  const [loading, setLoading] = useState(true)

  // Use stub data
  const allThreads = useMemo(() => emailThreadsStub, [])
  const monthThreads = useMemo(() => getStubThreadsByMonth(), [])

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false)
    }, 800) // 800ms delay to simulate loading

    return () => clearTimeout(timer)
  }, [])

  return {
    loading,
    error: undefined,
    data: allThreads,
    monthThreads,
    hasNextPage: false, // No pagination in stub data
    loadMore: () => {}, // No-op for stub data
  }
}

/**
 * Hook to use stub thread data for search
 *
 * This mimics the behavior of useSearchThreads hook
 */
export function useStubSearchThreads(
  searchTerm?: string,
  dateRanges?: {start: string; end: string}[],
  participantProfileIds?: string[],
  page: number = 0,
  itemsPerPage: number = 20
) {
  const [loading, setLoading] = useState(true)
  const [currentPageThreads, setCurrentPageThreads] = useState<
    ThreadWithProfiles[]
  >([])

  // Search all threads based on search term
  const allMatchingThreads = useMemo(() => {
    if (
      !searchTerm &&
      (!dateRanges || dateRanges.length === 0) &&
      (!participantProfileIds || participantProfileIds.length === 0)
    ) {
      return []
    }

    // For now, only implement text search
    return searchStubThreads(searchTerm || "")
  }, [searchTerm, dateRanges, participantProfileIds])

  // Handle pagination
  useEffect(() => {
    setLoading(true)

    // Simulate API call delay
    const timer = setTimeout(() => {
      const startIndex = page * itemsPerPage
      const endIndex = startIndex + itemsPerPage
      setCurrentPageThreads(allMatchingThreads.slice(startIndex, endIndex))
      setLoading(false)
    }, 800)

    return () => clearTimeout(timer)
  }, [allMatchingThreads, page, itemsPerPage])

  return {
    threads: allMatchingThreads,
    currentPageThreads,
    isLoading: loading,
    hasMore: (page + 1) * itemsPerPage < allMatchingThreads.length,
    error: undefined,
    page,
    totalCount: allMatchingThreads.length,
  }
}

/**
 * Hook to get a specific email thread by ID from stub data
 *
 * This mimics the behavior of useGetEmailThread hook
 */
export function useStubEmailThread(thread_uid: string) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | undefined>(undefined)
  const [data, setData] = useState<EmailThread | null>(null)

  useEffect(() => {
    if (!thread_uid) {
      setLoading(false)
      setData(null)
      return
    }

    setLoading(true)

    // Simulate API call delay
    const timer = setTimeout(() => {
      try {
        const foundThread = emailThreadsStub.find(
          (thread) => thread.thread_uid === thread_uid
        )
        setData(foundThread || null)
        setLoading(false)
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Unknown error"))
        setLoading(false)
      }
    }, 800)

    return () => clearTimeout(timer)
  }, [thread_uid])

  // Extract and format the relevant data similar to the real hook
  const title = data?.title || ""
  const subject = data?.messages?.[0]?.subject || ""
  const description = data?.description || ""
  const lastTimestamp = data?.last_message_timestamp || ""
  const createdAt = data?.created_at || ""
  const threadParticipants = data?.profiles || []
  const provider = data?.messages?.[0]?.provider_type || "email"
  const messages = data?.messages || []

  return {
    data,
    title,
    subject,
    description,
    lastTimestamp,
    createdAt,
    threadParticipants,
    provider,
    messages,
    loading,
    error,
  }
}
